import { z } from "zod"
import {
  firstNameField,
  lastNameField,
  emailField,
  phoneNumberField,
  postalCodeField,
  houseNumberField,
  streetField,
  cityField,
  bsnField,
  GenderEnum,
  learningPathPipEnumSchema,
  nullableInput,
  PHONE_NUMBER_REGEX,
  StudentEnrollmentTypeSchema,
  birthdateField,
  targetLevelSchema,
} from "../../index"

export interface PersonalStepProject {
  type?: string
  isBsnRequired?: boolean
  isStudentFileNumberRequired?: boolean
}

export type PersonalStepProjectSchemaType = ReturnType<
  typeof determineProjectSchemaType
>

export function determineProjectSchemaType(
  project: Pick<PersonalStepProject, "type" | "isBsnRequired"> | undefined,
) {
  if (project?.type === "inburgering") {
    return "inburgering"
  } else if (project?.type === "web" && project?.isBsnRequired) {
    return "webWithRequiredBSN"
  } else {
    return "default"
  }
}
// Base fields shared by all schemas
export const baseFields = {
  studentId: z.number().nullable(),
  firstName: firstNameField,
  lastName: lastNameField,
  birthDate: birthdateField,

  gender: GenderEnum.nullable().optional(),
  email: emailField,
  secondaryEmail: z
    .string()
    .email()
    .optional()
    .or(z.literal("").optional())
    .nullable(),

  phoneNumberCall: phoneNumberField,
  phoneNumberWhatsapp: z
    .string()
    .regex(PHONE_NUMBER_REGEX)
    .or(z.literal(""))
    .nullable(),
  samePhone: z.boolean().optional().default(true),

  contactPersonRelationship: z.string().optional().or(z.literal("")),

  mustPlanIntake: z.boolean().nullable().optional(),

  enrollmentType: StudentEnrollmentTypeSchema,

  postalCode: postalCodeField,
  houseNumber: houseNumberField,
  houseNumberAddition: z.string().nullable().optional(),
  street: streetField,
  city: cityField,

  // Optional fields
  nationality: z.string().optional(),
  countryOfBirth: z.string().optional().nullable(),
  countryOfOrigin: z.string().optional().nullable(),
  nativeLanguage: z.string().optional().nullable(),
  otherLanguages: z.array(z.string()).nullable().optional(),
  citizenServiceNumber: bsnField.optional().nullable(),
  comments: z.string().optional().or(z.literal("")),

  // Integration fields (optional by default)
  targetLevelPip: targetLevelSchema.optional().nullable(),
  learningPathPip: z.null(),
  civicIntegrationStartDate: z.string().optional(),
  civicIntegrationEndDate: z.string().optional().nullable(),

  civicIntegrationObligation: nullableInput(
    z.boolean({
      message: "Dit veld is verplicht",
    }),
  ),

  studentHasExternalHours: z.boolean().optional().default(false),
  requiresExternalHours: z.boolean().optional().default(false),

  numberOfLanguageHours: z.number().nullable().optional(),

  intakeResultReportFile: z.union([z.array(z.instanceof(File)), z.null()]),
}

const baseSchema = z.object({
  ...baseFields,
  projectSchemaType: z.literal("default"),
})

const integrationSchema = z.object({
  ...baseFields,
  projectSchemaType: z.literal("inburgering"),
  learningPathPip: learningPathPipEnumSchema.nullable().optional(),
  citizenServiceNumber: bsnField.optional().nullable(),
})

// Bij inburgering altijd, bij WEB als aangegeven in contract
const citizenServiceNumberRequiredWeb = z.object({
  ...baseFields,
  projectSchemaType: z.literal("webWithRequiredBSN"),
  citizenServiceNumber: bsnField.optional().nullable(),
})

export const personalStepSchema = z
  .discriminatedUnion("projectSchemaType", [
    integrationSchema,
    citizenServiceNumberRequiredWeb,
    baseSchema,
  ])
  .superRefine((data, ctx) => {
    if (
      data.projectSchemaType === "inburgering" &&
      data.civicIntegrationObligation === true &&
      !data.learningPathPip
    ) {
      ctx.addIssue({
        path: ["learningPathPip"],
        code: z.ZodIssueCode.custom,
        message: "Dit veld is verplicht",
      })
    }

    if (
      data.enrollmentType === "existing" ||
      data.enrollmentType === "transferred"
    ) {
      if (data.mustPlanIntake === null || data.mustPlanIntake === undefined) {
        ctx.addIssue({
          path: ["mustPlanIntake"],
          code: z.ZodIssueCode.custom,
          message: "Dit veld is verplicht",
        })
      }
    }

    const requiresExternalHours = !!data.requiresExternalHours

    if (
      requiresExternalHours &&
      (data.studentHasExternalHours === null ||
        data.studentHasExternalHours === undefined)
    ) {
      ctx.addIssue({
        path: ["studentHasExternalHours"],
        code: z.ZodIssueCode.custom,
        message: "Dit veld is verplicht voor dit contract",
      })
    }

    if (
      data.studentHasExternalHours &&
      (data.numberOfLanguageHours === null ||
        data.numberOfLanguageHours === undefined)
    ) {
      ctx.addIssue({
        path: ["numberOfLanguageHours"],
        code: z.ZodIssueCode.custom,
        message: "Dit veld is verplicht",
      })
    }
  })

export type PersonalStepSchemaInput = z.input<typeof personalStepSchema>
export type PersonalStepDraftData = Omit<
  Partial<PersonalStepSchemaInput>,
  "intakeResultReportFile"
>
export type PersonalStepSchemaOutput = z.infer<typeof personalStepSchema>
